import type {
  AttachComplianceDocumentInput,
  ComplianceDocumentContentType,
  CreateTruckInput,
  Page,
  PresignComplianceDocumentInput,
  TransferAssetOwnershipInput,
  TruckComplianceStatus,
  TruckListQuery,
  TruckStatus,
  UpdateTruckInput,
} from '@chukua/common';
import { api } from '../../lib/api';
import { type ComplianceDocumentView, type PresignedUpload, uploadToPresignedUrl } from '../../lib/compliance-documents';
import { toListParams } from '../../lib/list-params';

export interface TruckListItem {
  id: string;
  registration: string;
  classCode: string;
  make: string;
  model: string;
  capacityTonnes: number;
  compliance: TruckComplianceStatus;
  status: TruckStatus;
  createdAt: string;
}

export interface TruckView {
  id: string;
  registration: string;
  classCode: string;
  make: string;
  model: string;
  year: number;
  vin: string;
  engineNumber: string;
  capacityTonnes: number;
  tankCapacityLitres: number;
  hasGenset: boolean;
  isBonded: boolean;
  isHazardousCertified: boolean;
  isCrossBorderEquipped: boolean;
  currentOdometerKm: number;
  odometerRecordedAt: string;
  gpsDeviceId: string | null;
  status: TruckStatus;
  createdAt: string;
  updatedAt: string;
}

export interface TruckStatusHistoryEntry {
  id: string;
  fromStatus: TruckStatus;
  toStatus: TruckStatus;
  causeEvent: string;
  occurredAt: string;
}

export interface TruckDetail extends TruckView {
  documents: ComplianceDocumentView[];
  complianceStatus: TruckComplianceStatus;
  statusHistory: TruckStatusHistoryEntry[];
}

export interface TruckOwnershipPeriod {
  id: string;
  ownerId: string;
  ownerName: string;
  effectiveFrom: string;
  effectiveTo: string | null;
}

export function listTrucks(query: TruckListQuery): Promise<Page<TruckListItem>> {
  return api<Page<TruckListItem>>(`/trucks?${toListParams(query)}`);
}

export function createTruck(input: CreateTruckInput): Promise<TruckView> {
  return api<TruckView>('/trucks', { method: 'POST', body: JSON.stringify(input) });
}

export function getTruck(id: string): Promise<TruckDetail> {
  return api<TruckDetail>(`/trucks/${id}`);
}

export function updateTruck(id: string, input: UpdateTruckInput): Promise<TruckView> {
  return api<TruckView>(`/trucks/${id}`, { method: 'PATCH', body: JSON.stringify(input) });
}

export function recordOdometerReading(id: string, odometerKm: number): Promise<TruckView> {
  return api<TruckView>(`/trucks/${id}/odometer-readings`, { method: 'POST', body: JSON.stringify({ odometerKm }) });
}

export function presignTruckDocument(truckId: string, input: PresignComplianceDocumentInput): Promise<PresignedUpload> {
  return api<PresignedUpload>(`/trucks/${truckId}/documents/presign`, { method: 'POST', body: JSON.stringify(input) });
}

/** Presign then upload in one call, so a hook only awaits the result — the key and content type it wrote to. */
export async function uploadTruckDocumentFile(truckId: string, file: File): Promise<{ key: string; contentType: ComplianceDocumentContentType }> {
  const contentType = file.type as ComplianceDocumentContentType;
  const upload = await presignTruckDocument(truckId, { contentType });
  await uploadToPresignedUrl(upload, file);
  return { key: upload.key, contentType };
}

/** Add or renew — the server resolves which by whether a current document already exists for this type. Callers reload the truck detail afterward. */
export async function attachTruckDocument(truckId: string, input: AttachComplianceDocumentInput): Promise<void> {
  await api<void>(`/trucks/${truckId}/documents`, { method: 'POST', body: JSON.stringify(input) });
}

export function getTruckDocumentDownloadUrl(truckId: string, documentId: string): Promise<{ url: string }> {
  return api<{ url: string }>(`/trucks/${truckId}/documents/${documentId}/download-url`);
}

export function listTruckOwnershipPeriods(truckId: string): Promise<TruckOwnershipPeriod[]> {
  return api<TruckOwnershipPeriod[]>(`/trucks/${truckId}/ownership-periods`);
}

export async function transferTruckOwnership(truckId: string, input: TransferAssetOwnershipInput): Promise<void> {
  await api<void>(`/trucks/${truckId}/ownership-periods/transfer`, { method: 'POST', body: JSON.stringify(input) });
}
